import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { 
  Search, 
  MapPin, 
  ChevronDown, 
  Filter, 
  Award, 
  DollarSign, 
  Clock, 
  Shield, 
  Users as UsersIcon,
  Star,
  Sparkles,
  Zap,
  Target,
  CheckCircle,
  Plus,
  Minus,
  ArrowRight,
  ArrowLeft,
  Loader2,
  Eye,
  User,
  Globe,
  Wifi
} from 'lucide-react';
import { ZipCodeSelect } from '../components/ui/ZipCodeSelect';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Therapist } from '../types/clinician';
import { useFetchInsuranceResource } from '../hooks/Resources/insurance/useFetchInsuranceResource';
import { AppDispatch, RootState } from '../redux';
import { fetchTherapists } from '../redux/therapist/therapistSlice';
import { useProfileTracking } from '../hooks/useProfileTracking';
// import useFetchInsurance from '../hooks/Resources/insurance/useFetchInsurance';

type ProviderTherapist = Therapist & {
  _id?: string;
  description?: string;
  rate?: string;
  approaches?: string[];
  languages?: string[];
  score?: number;
};

// Comprehensive specialty list matching the HTML
const COMPREHENSIVE_SPECIALTIES = [
  "Addiction", "ADHD", "Adoption", "Alcohol Use", "Anger Management", "Anxiety", "Autism",
  "Behavioral Issues", "Bipolar Disorder", "Body Positivity", "Borderline Personality (BPD)",
  "Cancer", "Career Counseling", "Child", "Chronic Illness", "Chronic Pain", "Codependency",
  "Dementia", "Depression", "Dissociative Disorders (DID)", "Divorce", "Domestic Abuse",
  "Drug Abuse", "Dual Diagnosis", "Eating Disorders", "Education and Learning Disabilities",
  "First Responders", "Gambling", "Geriatric and Seniors", "Grief", "Hoarding", "Infertility",
  "Infidelity", "Intellectual Disability", "Internet Addiction", "Life Coaching",
  "Marriage Counseling", "Medical Detox", "Medication Management", "Men's Issues",
  "Narcissistic Personality (NPD)", "Obesity", "Obsessive-Compulsive (OCD)",
  "Open Relationships Non-Monogamy", "Oppositional Defiance (ODD)", "Parenting",
  "Personality Disorders", "Pregnancy, Prenatal, Postpartum", "Psychosis", "Racial Identity",
  "Relationship Issues", "Self Esteem", "Self-Harming", "Sex Therapy", "Sex-Positive, Kink Allied",
  "Sexual Abuse", "Sexual Addiction", "Sleep or Insomnia", "Spirituality", "Sports Performance",
  "Stress", "Substance Use", "Suicidal Ideation", "Testing and Evaluation", "Transgender",
  "Trauma and PTSD", "Traumatic Brain Injury (TBI)", "Veterans", "Video Game Addiction",
  "Weight Loss", "Women's Issues"
];

// Popular searches for the bottom section
const POPULAR_SEARCHES = [
  "Addiction", "ADHD", "Adoption", "Alcohol Use", "Anger Management", "Online Therapy", "Available Today"
];

const SESSION_TYPE_OPTIONS = [
  'Couples therapy',
  'Family therapy',
  'Group therapy',
  'Individual therapy',
];

const SESSION_MODE_OPTIONS = [
  { value: 'in-person', label: 'In-Person', icon: '🏢' },
  { value: 'virtual',   label: 'Virtual',   icon: '💻' },
];

const LANGUAGE_OPTIONS = ['Arabic', 'English', 'French', 'German', 'Mandarin', 'Spanish'];

const MODALITY_OPTIONS = [
  'Acceptance and Commitment (ACT)',
  'Cognitive Behavioral (CBT)',
  'Dialectical Behavior (DBT)',
  'EMDR',
  'Humanistic',
  'Mindfulness-Based',
  'Psychodynamic',
  'Solution-Focused',
];

//#region Animations
// Animation variants for smooth transitions
const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  show: { 
    opacity: 1, 
    y: 0,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 20,
      duration: 0.6
    }
  },
  hover: {
    y: -5,
    scale: 1.02,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 20
    }
  }
};

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  show: { 
    opacity: 1, 
    y: 0,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 20
    }
  }
};

const slideInRight = {
  hidden: { opacity: 0, x: 20 },
  show: { 
    opacity: 1, 
    x: 0,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 20
    }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.8 },
  show: { 
    opacity: 1, 
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 200,
      damping: 20
    }
  }
};

//#endregion

/** Stable compare for query strings so we don't navigate when only param order differs. */
function normalizeProvidersQueryString(search: string): string {
  const raw = search.startsWith('?') ? search.slice(1) : search;
  const p = new URLSearchParams(raw);
  return [...p.entries()]
    .sort((a, b) =>
      a[0] === b[0] ? String(a[1]).localeCompare(String(b[1])) : a[0].localeCompare(b[0])
    )
    .map(([k, v]) => `${k}=${v}`)
    .join('&');
}

type FindTherapistParsed = {
  specialty: string;
  locationInput: string;
  insurance: string;
  searchQuery: string;
  zipFromUrl: string;
  sessionRate: string;
  sessionTypes: string[];
  sessionModes: string[];
  languages: string[];
  modalities: string[];
  page: number;
};

function parseFindTherapistSearch(search: string): FindTherapistParsed {
  const sp = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  return {
    specialty: sp.get('specialty') || '',
    locationInput: sp.get('location') || '',
    insurance: sp.get('insurance') || '',
    searchQuery: sp.get('search') || '',
    zipFromUrl: sp.get('zipCode') || sp.get('location') || '',
    sessionRate: sp.get('sessionRate') || '',
    sessionTypes: sp.get('sessionTypes') ? sp.get('sessionTypes')!.split(',').filter(Boolean) : [],
    sessionModes: sp.get('sessionModes') ? sp.get('sessionModes')!.split(',').filter(Boolean) : [],
    languages: sp.get('languages') ? sp.get('languages')!.split(',').filter(Boolean) : [],
    modalities: sp.get('modalities') ? sp.get('modalities')!.split(',').filter(Boolean) : [],
    page: parseInt(sp.get('page') || '1', 10),
  };
}

function listsEqualCsv(csv: string, arr: string[]): boolean {
  const a = csv.split(',').filter(Boolean).sort().join(',');
  const b = [...arr].filter(Boolean).sort().join(',');
  return a === b;
}

/** True when the address bar already matches filter state (encoding/order tolerant). */
function urlMatchesFindTherapistFilters(
  parsed: FindTherapistParsed,
  cur: {
    specialty: string;
    locationInput: string;
    insurance: string;
    searchQuery: string;
    zipCode: string;
    sessionRate: string;
  sessionTypes: string[];
  sessionModes: string[];
  languages: string[];
  modalities: string[];
  currentPage: number;
  }
): boolean {
  const zipFromState = cur.zipCode || cur.locationInput;
  const serverMismatch =
    parsed.specialty !== cur.specialty ||
    parsed.locationInput !== cur.locationInput ||
    parsed.insurance !== cur.insurance ||
    parsed.searchQuery !== cur.searchQuery ||
    parsed.zipFromUrl !== zipFromState ||
    parsed.page !== cur.currentPage;
  const clientMismatch =
    parsed.sessionRate !== cur.sessionRate ||
    !listsEqualCsv(parsed.sessionTypes.join(','), cur.sessionTypes) ||
    !listsEqualCsv(parsed.sessionModes.join(','), cur.sessionModes) ||
    !listsEqualCsv(parsed.languages.join(','), cur.languages) ||
    !listsEqualCsv(parsed.modalities.join(','), cur.modalities);
  return !serverMismatch && !clientMismatch;
}

export const ProvidersPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const {
    insurances,
    isLoading: insurancesLoading,
    error: insurancesError,
    fetchInsuranceData,
  } = useFetchInsuranceResource();

  useEffect(() => {
    void fetchInsuranceData();
  }, [fetchInsuranceData]);
  const therapists = useSelector((state: RootState) => state.therapist.therapist || []);
  const pagination = useSelector((state: RootState) => state.therapist.pagination);
  const loading = useSelector((state: RootState) => state.therapist.loading);

  // Parse URL parameters
  const urlParams = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return {
      specialty: searchParams.get('specialty') || '',
      location: searchParams.get('location') || '',
      insurance: searchParams.get('insurance') || '',
      query: searchParams.get('search') || '',
      zip: searchParams.get('zipCode') || '',
      sessionRate: searchParams.get('sessionRate') || '',
      sessionTypes: searchParams.get('sessionTypes') || '',
      sessionModes: searchParams.get('sessionModes') || '',
      languages: searchParams.get('languages') || '',
      modalities: searchParams.get('modalities') || '',
      page: parseInt(searchParams.get('page') || '1', 10)
    };
  }, [location.search]);

  // State management
  const [filters, setFilters] = useState({
    specialty: urlParams.specialty,
    locationInput: urlParams.location,
    insurance: urlParams.insurance,
    searchQuery: urlParams.query,
    zipCode: urlParams.zip || urlParams.location,
    sessionRate: urlParams.sessionRate,
    sessionTypes: urlParams.sessionTypes ? urlParams.sessionTypes.split(',').filter(Boolean) : [],
    sessionModes: urlParams.sessionModes ? urlParams.sessionModes.split(',').filter(Boolean) : [],
    languages: urlParams.languages ? urlParams.languages.split(',').filter(Boolean) : [],
    modalities: urlParams.modalities ? urlParams.modalities.split(',').filter(Boolean) : [],
    currentPage: urlParams.page,
    sortBy: 'best-match',
    showAdditionalFilters: false
  });


  // Destructure for easier access
  const { specialty, locationInput, insurance, searchQuery, zipCode, sessionRate, sessionTypes, sessionModes, languages, modalities, currentPage, sortBy, showAdditionalFilters } = filters;
  const isFiltering = loading;

  // Latest filters for URL↔state logic without stale closures
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  /** After we navigate to sync state→URL, ignore one stale `location` read in useEffect (fixes double-fetch / "reload"). */
  const pendingUrlNormRef = useRef<string | null>(null);

  /** Drops duplicate /fetch calls within a short window (handler + URL effect + Strict Mode double-mount). */
  const lastSearchDedupeRef = useRef<{ signature: string; at: number } | null>(null);

  const buildSearchParams = useCallback((overrides: Partial<{
    specialty: string;
    zipCode: string;
    insurance: string;
    searchQuery: string;
    sessionRate: string;
    sessionTypes: string[];
    sessionModes: string[];
    languages: string[];
    modalities: string[];
  }> = {}) => {
    const effective = {
      specialty,
      zipCode,
      insurance,
      searchQuery,
      sessionRate,
      sessionTypes,
      sessionModes,
      languages,
      modalities,
      ...overrides
    };

    return {
      search: effective.searchQuery || undefined,
      speciality: effective.specialty || undefined,
      zipCode: effective.zipCode || undefined,
      insurance: effective.insurance || undefined,
      sessionRate: effective.sessionRate || undefined,
      sessionTypes: effective.sessionTypes?.length
        ? JSON.stringify({ isActive: true, sessionTypes: effective.sessionTypes })
        : undefined,
      sessionMode: effective.sessionModes?.length
        ? JSON.stringify({ isActive: true, sessionModes: effective.sessionModes })
        : undefined,
      languages: effective.languages?.length ? effective.languages.join(',') : undefined,
      modalities: effective.modalities?.length ? effective.modalities.join(',') : undefined,
    };
  }, [specialty, zipCode, insurance, searchQuery, sessionRate, sessionTypes, sessionModes, languages, modalities]);

  const runSearch = useCallback(
    async (
      page = 1,
      overrides: Partial<{
        specialty: string;
        zipCode: string;
        insurance: string;
        searchQuery: string;
        sessionRate: string;
        sessionTypes: string[];
        sessionModes: string[];
        languages: string[];
        modalities: string[];
      }> = {}
    ) => {
      const params = {
        ...buildSearchParams(overrides),
        page,
        limit: 12
      };

      await dispatch(fetchTherapists(params));
    },
    [buildSearchParams, dispatch]
  );

  // Track viewed profiles to avoid duplicate tracking
  const viewedProfilesRef = React.useRef<Set<string>>(new Set());

  // Debug logging
  useEffect(() => {
  }, [therapists, loading, searchQuery, specialty, zipCode, currentPage]);

  // Initial fetch once on mount (do not tie to `runSearch` identity — that duplicated every API call when specialty/zip/etc. changed)
  const didInitialFetch = React.useRef(false);
  useEffect(() => {
    if (didInitialFetch.current) return;
    didInitialFetch.current = true;
    const parsed = parseFindTherapistSearch(location.search);
    void runSearch(parsed.page, {
      specialty: parsed.specialty,
      zipCode: parsed.zipFromUrl,
      insurance: parsed.insurance,
      searchQuery: parsed.searchQuery,
      sessionRate: parsed.sessionRate,
      sessionTypes: parsed.sessionTypes,
      sessionModes: parsed.sessionModes,
      languages: parsed.languages,
      modalities: parsed.modalities,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional one-time mount fetch
  }, []);

  // API applies specialty/location/insurance/keywords + additional filters; we only sort client-side.
  const filteredClinicians = useMemo(() => {
    const clinicians = Array.isArray(therapists) ? [...(therapists as ProviderTherapist[])] : [];

    if (sortBy === 'price-low') {
      clinicians.sort(
        (a: ProviderTherapist & { rateMin?: number }, b: ProviderTherapist & { rateMin?: number }) =>
          (a?.rateMin ?? 0) - (b?.rateMin ?? 0)
      );
    } else if (sortBy === 'price-high') {
      clinicians.sort(
        (a: ProviderTherapist & { rateMax?: number }, b: ProviderTherapist & { rateMax?: number }) =>
          (b?.rateMax ?? 0) - (a?.rateMax ?? 0)
      );
    } else if (sortBy === 'available') {
      clinicians.sort(
        (
          a: ProviderTherapist & { availability?: { open?: unknown[] } },
          b: ProviderTherapist & { availability?: { open?: unknown[] } }
        ) => (b?.availability?.open?.length ?? 0) - (a?.availability?.open?.length ?? 0)
      );
    }

    return clinicians;
  }, [therapists, sortBy]);

  const totalResults = pagination?.total || filteredClinicians.length;
  const currentPageNum = pagination?.page || 1;
  const totalPages = pagination?.totalPages || 1;

  // Helper to update filters
  const updateFilters = useCallback((newFilters: Partial<typeof filters>) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters
    }));
  }, []);

  // Push filter state → URL in layout phase so `location` is updated before URL-sync useEffect runs (avoids stale URL + fresh state mismatch).
  useLayoutEffect(() => {
    const searchParams = new URLSearchParams();

    if (specialty) searchParams.set('specialty', specialty);
    if (locationInput) searchParams.set('location', locationInput);
    if (insurance) searchParams.set('insurance', insurance);
    if (searchQuery) searchParams.set('search', searchQuery);
    if (zipCode) searchParams.set('zipCode', zipCode);
    if (sessionRate) searchParams.set('sessionRate', sessionRate);
    if (sessionTypes.length) searchParams.set('sessionTypes', sessionTypes.join(','));
    if (sessionModes.length) searchParams.set('sessionModes', sessionModes.join(','));
    if (languages.length) searchParams.set('languages', languages.join(','));
    if (modalities.length) searchParams.set('modalities', modalities.join(','));
    if (currentPage > 1) searchParams.set('page', currentPage.toString());

    const nextRaw = searchParams.toString();
    const nextNorm = normalizeProvidersQueryString(nextRaw);
    const locNorm = normalizeProvidersQueryString(location.search);

    if (nextNorm === locNorm) return;

    pendingUrlNormRef.current = nextNorm;
    const pendingSnapshot = nextNorm;
    const safetyTimer = window.setTimeout(() => {
      if (pendingUrlNormRef.current === pendingSnapshot) {
        pendingUrlNormRef.current = null;
      }
    }, 1500);
    navigate(
      { pathname: location.pathname, search: nextRaw ? `?${nextRaw}` : '' },
      { replace: true }
    );
    return () => clearTimeout(safetyTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- omit location.search so back/forward isn't overwritten by this effect
  }, [
    specialty,
    locationInput,
    insurance,
    searchQuery,
    zipCode,
    sessionRate,
    sessionTypes,
    sessionModes,
    languages,
    modalities,
    currentPage,
    location.pathname,
    navigate,
  ]);

  // URL → state: back/forward, shared links. Skip while we're waiting for our own navigate to land (pending ref).
  useEffect(() => {
    const locNorm = normalizeProvidersQueryString(location.search);
    const parsed = parseFindTherapistSearch(location.search);
    const cur = filtersRef.current;

    if (pendingUrlNormRef.current !== null) {
      const normMatch = locNorm === pendingUrlNormRef.current;
      const semanticMatch = urlMatchesFindTherapistFilters(parsed, cur);
      if (normMatch || semanticMatch) {
        pendingUrlNormRef.current = null;
      }
      return;
    }

    const zipFromState = cur.zipCode || cur.locationInput;

    const serverMismatch =
      parsed.specialty !== cur.specialty ||
      parsed.locationInput !== cur.locationInput ||
      parsed.insurance !== cur.insurance ||
      parsed.searchQuery !== cur.searchQuery ||
      parsed.zipFromUrl !== zipFromState ||
      parsed.page !== cur.currentPage;

    const clientMismatch =
      parsed.sessionRate !== cur.sessionRate ||
    !listsEqualCsv(parsed.sessionTypes.join(','), cur.sessionTypes) ||
    !listsEqualCsv(parsed.sessionModes.join(','), cur.sessionModes) ||
    !listsEqualCsv(parsed.languages.join(','), cur.languages) ||
      !listsEqualCsv(parsed.modalities.join(','), cur.modalities);

    if (!serverMismatch && !clientMismatch) return;

    setFilters((prev) => ({
      ...prev,
      specialty: parsed.specialty,
      locationInput: parsed.locationInput,
      insurance: parsed.insurance,
      searchQuery: parsed.searchQuery,
      zipCode: parsed.zipFromUrl || '',
      sessionRate: parsed.sessionRate,
      sessionTypes: parsed.sessionTypes,
      sessionModes: parsed.sessionModes,
      languages: parsed.languages,
      modalities: parsed.modalities,
      currentPage: parsed.page,
    }));

    if (serverMismatch || clientMismatch) {
      void runSearch(parsed.page, {
        specialty: parsed.specialty,
        zipCode: parsed.zipFromUrl,
        insurance: parsed.insurance,
        searchQuery: parsed.searchQuery,
        sessionRate: parsed.sessionRate,
        sessionTypes: parsed.sessionTypes,
        sessionModes: parsed.sessionModes,
        languages: parsed.languages,
        modalities: parsed.modalities,
      });
    }
  }, [location.search, runSearch]);

  const handleMultiSelect = (field: 'languages' | 'modalities' | 'sessionTypes' | 'sessionModes', value: string, checked: boolean) => {
    const nextList = checked
      ? [...filters[field], value]
      : filters[field].filter((item) => item !== value);
    updateFilters({ [field]: nextList, currentPage: 1 });
    void runSearch(1, { [field]: nextList });
  };

  // Event handlers
  const handleSpecialtyChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSpecialty = e.target.value;
    updateFilters({ specialty: newSpecialty, currentPage: 1 });
    await runSearch(1, { specialty: newSpecialty });
  };

  const handleLocationChange = async (zipCode: string) => {
    updateFilters({ locationInput: zipCode, zipCode: zipCode, currentPage: 1 });
    await runSearch(1, { zipCode });
  };

  const handleInsuranceChange = async (newInsurance: string) => {
    updateFilters({ insurance: newInsurance, currentPage: 1 });
    await runSearch(1, { insurance: newInsurance });
  };

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    updateFilters({ sortBy: e.target.value });
  };

  const handleSessionModeToggle = (mode: string) => {
    const next = sessionModes.includes(mode)
      ? sessionModes.filter((m) => m !== mode)
      : [...sessionModes, mode];
    updateFilters({ sessionModes: next, currentPage: 1 });
    void runSearch(1, { sessionModes: next });
  };

  const handleSearch = async () => {
    updateFilters({ currentPage: 1 });
    await runSearch(1);
  };

  const handlePopularSearch = async (searchTerm: string) => {
    updateFilters({ specialty: searchTerm, currentPage: 1 });
    await runSearch(1, { specialty: searchTerm });
  };

  // Get practiceId from localStorage
  const practiceId = useMemo(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return user?.["custom:id_practice"] || user?.practice_id || null;
    } catch (error) {
      console.error('Error getting practiceId from localStorage:', error);
      return null;
    }
  }, []);

  // Use profile tracking hook
  const { trackProfileClick } = useProfileTracking({
    specialty,
    location: locationInput,
    insurance
  }, practiceId || undefined);

  // Wrapper function for backward compatibility
  const trackProfileButtonClick = useCallback(async (buttonType: string, additionalData: Record<string, unknown> = {}) => {
    await trackProfileClick({
      buttonType,
      additionalData
    });
  }, [trackProfileClick]);

  // Track profile views when cards become visible using Intersection Observer
  useEffect(() => {
    if (!filteredClinicians.length || loading) return;

    // Reset viewed profiles when therapists change
    viewedProfilesRef.current.clear();

    // Set up Intersection Observer to track when therapist cards become visible
    const observerOptions = {
      root: null, // viewport
      rootMargin: '0px',
      threshold: 0.5 // Trigger when 50% of the card is visible
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const therapistId = entry.target.getAttribute('data-therapist-id');
          const therapistIndex = entry.target.getAttribute('data-therapist-index');
          
          if (therapistId && !viewedProfilesRef.current.has(therapistId)) {
            // Find the therapist data
            const therapist = filteredClinicians.find(
              (t: ProviderTherapist) => t.id === therapistId || t._id === therapistId
            );
            
            if (therapist) {
              viewedProfilesRef.current.add(therapistId);
              
              // Track profile view automatically
              trackProfileButtonClick('profile_view', {
                clinicianId: therapist._id,
                clinicianName: therapist.fullName,
                clinicianSlug: therapist.slug,
                matchType: 'therapist_card_view',
                position: `card_${therapistIndex || 'unknown'}`,
                therapistIndex: therapistIndex ? parseInt(therapistIndex) : undefined,
                viewType: 'automatic_impression',
                practiceId: therapist.practice_id || therapist.id_practice,
                score: therapist.score,
              });
            }
          }
        }
      });
    }, observerOptions);

    // Small delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      // Observe all therapist cards
      const cards = document.querySelectorAll('[data-therapist-card]');
      cards.forEach(card => observer.observe(card));
    }, 100);

    // Cleanup
    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [filteredClinicians, loading, trackProfileButtonClick]);

  const handlePageChange = async (page: number) => {
    updateFilters({ currentPage: page });
    await runSearch(page);
  };

  return (
    <>
      <Helmet>
        <title>Find a Therapist Near You | Licensed Mental Health Professionals</title>
        <meta name="description" content="Find licensed therapists, counselors, and mental health professionals in your area. Search by specialty, insurance, location, and availability. Get matched with the right therapist for your needs." />
        <meta name="keywords" content="find therapist, mental health professional, counselor, therapist near me, online therapy, licensed therapist, mental health services" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Find a Therapist Near You | Licensed Mental Health Professionals" />
        <meta property="og:description" content="Connect with licensed mental health professionals who can help you on your journey to wellness. Search by specialty, insurance, and location." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://therapistdirect.com/find-therapist" />
        <link rel="canonical" href="https://therapistdirect.com/find-therapist" />
        {/* Schema.org structured data for local business */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "TherapistDirect",
            "description": "Find licensed therapists and mental health professionals",
            "url": "https://therapistdirect.com/find-therapist",
            "serviceType": "Mental Health Services",
            "areaServed": "United States",
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Therapy Services",
              "itemListElement": [
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Individual Therapy"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Couples Counseling"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Family Therapy"
                  }
                }
              ]
            }
          })}
        </script>
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 relative overflow-hidden">
        {/* Floating Particles Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            className="absolute top-20 left-10 w-2 h-2 bg-purple-400 rounded-full opacity-30"
            animate={{
              y: [0, -20, 0],
              opacity: [0.3, 0.6, 0.3],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          <motion.div
            className="absolute top-40 right-20 w-3 h-3 bg-blue-400 rounded-full opacity-20"
            animate={{
              y: [0, -30, 0],
              opacity: [0.2, 0.5, 0.2],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1
            }}
          />
          <motion.div
            className="absolute bottom-40 left-1/4 w-1 h-1 bg-indigo-400 rounded-full opacity-40"
            animate={{
              y: [0, -15, 0],
              opacity: [0.4, 0.7, 0.4],
            }}
            transition={{
              duration: 2.5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 2
            }}
          />
        </div>

        {/* Header Section with Enhanced Glassmorphism */}
        <motion.div 
          className="bg-gradient-to-r from-purple-600 via-indigo-600 to-purple-700 text-white relative z-10"
          variants={fadeInUp}
          initial="hidden"
          animate="show"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <motion.div 
              className="text-center mb-8"
              variants={fadeInUp}
            >
              <motion.h1 
                className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-white to-purple-100 bg-clip-text text-transparent"
                variants={fadeInUp}
              >
                <Sparkles className="inline w-8 h-8 mr-3 text-yellow-300" />
                Find Your Perfect Provider
              </motion.h1>
              <motion.p 
                className="text-xl text-purple-100 max-w-2xl mx-auto"
                variants={fadeInUp}
              >
                Connect with licensed mental health professionals who can help you on your journey to wellness
              </motion.p>
            </motion.div>
            
            {/* Enhanced Search Form with Glassmorphism */}
            <motion.div 
              className="bg-white/10 backdrop-blur-md rounded-2xl p-6 md:p-8 shadow-2xl border border-white/20"
              variants={scaleIn}
            >
              <form
                className="contents"
                onSubmit={(e) => {
                  e.preventDefault();
                  void handleSearch();
                }}
              >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                {/* Specialty Select */}
                <div className="relative">
                  <label className="block text-sm font-semibold text-purple-100 mb-2">
                    <Target className="inline w-4 h-4 mr-1" />
                    Specialty
                  </label>
                  <div className="relative">
                    <select 
                      value={specialty}
                      onChange={handleSpecialtyChange}
                      className="w-full px-4 py-3 bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl text-white placeholder-purple-200 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50 transition-all duration-300 appearance-none pr-10"
                      disabled={isFiltering}
                    >
                      <option value="">Select Specialty</option>
                      {COMPREHENSIVE_SPECIALTIES.map((spec) => (
                        <option key={spec} value={spec} className="text-gray-800">
                          {spec}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-purple-200 pointer-events-none" />
                    {isFiltering && (
                      <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-purple-200 animate-spin" />
                    )}
                  </div>
                </div>

                {/* Location Input - Zip Code Select */}
                <div className="relative">
                  <label className="block text-sm font-semibold text-purple-100 mb-2">
                    <MapPin className="inline w-4 h-4 mr-1" />
                    Location
                  </label>
                  <ZipCodeSelect
                    value={zipCode || locationInput}
                    onChange={handleLocationChange}
                    placeholder="ZIP code, city, or area — paste or type"
                    disabled={isFiltering}
                    isLoading={isFiltering}
                  />
                </div>

                {/* Insurance Select (options loaded from /insurance API) */}
                <div className="relative">
                  <label className="block text-sm font-semibold text-purple-100 mb-2">
                    <Shield className="inline w-4 h-4 mr-1" />
                    Insurance
                  </label>
                  <div className="relative">
                    <select 
                      value={insurance}
                      onChange={(e) => handleInsuranceChange(e.target.value)}
                      className="w-full px-4 py-3 bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl text-white placeholder-purple-200 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50 transition-all duration-300 appearance-none pr-10"
                      disabled={isFiltering || insurancesLoading}
                    >
                      <option value="">
                        {insurancesLoading ? 'Loading insurance plans…' : 'Select insurance (optional)'}
                      </option>
                      {insurances.map((ins) => {
                        const id = String(ins.id ?? ins.company_name ?? '');
                        return (
                          <option key={id} value={ins.company_name} className="text-gray-800">
                            {ins.company_name}
                          </option>
                        );
                      })}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-purple-200 pointer-events-none" />
                    {(isFiltering || insurancesLoading) && (
                      <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-purple-200 animate-spin" />
                    )}
                  </div>
                  {insurancesError && (
                    <p className="mt-1 text-xs text-amber-200">
                      Couldn’t load insurance list. You can still search without insurance.
                    </p>
                  )}
                </div>
              </div>

              {/* Session Mode Filter — multi-select */}
              <div className="flex flex-col items-center gap-2 mb-5">
                <span className="text-sm font-semibold text-purple-100 flex items-center gap-1.5">
                  <Wifi className="w-4 h-4" />
                  Session Mode
                  {sessionModes.length > 0 && (
                    <span className="ml-1 text-xs bg-white/20 rounded-full px-2 py-0.5">{sessionModes.length}</span>
                  )}
                </span>
                <div className="flex gap-2 flex-wrap justify-center">
                  {SESSION_MODE_OPTIONS.map((opt) => {
                    const isActive = sessionModes.includes(opt.value);
                    return (
                      <motion.button
                        key={opt.value}
                        type="button"
                        disabled={isFiltering}
                        onClick={() => handleSessionModeToggle(opt.value)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold border transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                          isActive
                            ? 'bg-white text-purple-700 border-white shadow-lg shadow-purple-900/20'
                            : 'bg-white/15 text-white border-white/30 hover:bg-white/25 hover:border-white/50'
                        }`}
                      >
                        <span>{opt.icon}</span>
                        {opt.label}
                        {isActive && <span className="text-purple-600 font-bold">✓</span>}
                      </motion.button>
                    );
                  })}
                  {sessionModes.length > 0 && (
                    <motion.button
                      type="button"
                      disabled={isFiltering}
                      onClick={() => { updateFilters({ sessionModes: [], currentPage: 1 }); void runSearch(1, { sessionModes: [] }); }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="flex items-center gap-1 px-4 py-2.5 rounded-xl text-xs font-semibold border border-white/20 bg-white/10 text-white/70 hover:bg-white/20 transition-all duration-200"
                    >
                      Clear
                    </motion.button>
                  )}
                </div>
              </div>

              <p className="text-xs text-purple-100/85 text-center mb-4 max-w-2xl mx-auto leading-relaxed">
                <strong className="text-white/95">Combined search:</strong> specialty, location, insurance, session mode, and <strong className="text-white/95">additional filters</strong> (max rate, session type, languages, approaches) are sent to the server when you search or change those fields.
                <strong className="text-white/95"> Sort</strong> is applied on this page only.
              </p>

              {/* Search Button */}
              <div className="flex justify-center">
                <motion.button
                  type="submit"
                  disabled={isFiltering}
                  className="px-8 py-3 bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white font-semibold rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {isFiltering ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Searching...
                    </>
                  ) : (
                    <>
                      <Search className="w-5 h-5" />
                      Search with all filters
                    </>
                  )}
                </motion.button>
              </div>
              </form>

              {/* Additional Filters Toggle */}
              <motion.div 
                className="flex justify-center mt-4"
                variants={fadeInUp}
              >
                <motion.button
                  onClick={() => setFilters(prev => ({ ...prev, showAdditionalFilters: !prev.showAdditionalFilters }))}
                  className="flex items-center gap-2 text-purple-100 hover:text-white transition-colors duration-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Filter className="w-4 h-4" />
                  {showAdditionalFilters ? 'Hide' : 'Show'} Additional Filters
                  {showAdditionalFilters ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                </motion.button>
              </motion.div>

              <AnimatePresence>
                {showAdditionalFilters && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-5 overflow-hidden"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 rounded-xl bg-white/10 border border-white/20">
                      <div>
                        <label className="block text-sm font-semibold text-purple-100 mb-2">Max Session Rate ($)</label>
                        <input
                          type="number"
                          min="0"
                          value={sessionRate}
                          onChange={(e) => updateFilters({ sessionRate: e.target.value, currentPage: 1 })}
                          onBlur={(e) => {
                            void runSearch(1, { sessionRate: e.target.value });
                          }}
                          className="w-full px-4 py-2 bg-white/20 border border-white/30 rounded-xl text-white placeholder-purple-200 focus:outline-none focus:ring-2 focus:ring-white/50"
                          placeholder="e.g. 150"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-purple-100 mb-2">
                          Session Type
                          {sessionTypes.length > 0 && (
                            <span className="ml-2 text-xs font-normal bg-white/20 px-2 py-0.5 rounded-full">
                              {sessionTypes.length} selected
                            </span>
                          )}
                        </label>
                        <div className="grid grid-cols-1 gap-2">
                          {SESSION_TYPE_OPTIONS.map((type) => (
                            <label key={type} className="flex items-center gap-2 text-sm text-purple-100 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={sessionTypes.includes(type)}
                                onChange={(e) => handleMultiSelect('sessionTypes', type, e.target.checked)}
                                className="rounded border-white/40 bg-white/20 text-purple-600"
                              />
                              {type}
                            </label>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-purple-100 mb-2">Languages</label>
                        <div className="grid grid-cols-2 gap-2">
                          {LANGUAGE_OPTIONS.map((language) => (
                            <label key={language} className="flex items-center gap-2 text-sm text-purple-100">
                              <input
                                type="checkbox"
                                checked={languages.includes(language)}
                                onChange={(e) => handleMultiSelect('languages', language, e.target.checked)}
                                className="rounded border-white/40"
                              />
                              {language}
                            </label>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-purple-100 mb-2">Therapy Modalities</label>
                        <div className="grid grid-cols-1 gap-2 max-h-32 overflow-y-auto pr-1">
                          {MODALITY_OPTIONS.map((modality) => (
                            <label key={modality} className="flex items-center gap-2 text-sm text-purple-100">
                              <input
                                type="checkbox"
                                checked={modalities.includes(modality)}
                                onChange={(e) => handleMultiSelect('modalities', modality, e.target.checked)}
                                className="rounded border-white/40"
                              />
                              {modality}
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Popular Searches */}
            <motion.div 
              className="mt-8"
              variants={fadeInUp}
            >
              <div className="flex flex-wrap gap-2">
                <span className="text-purple-100 text-sm">Popular searches:</span>
                {POPULAR_SEARCHES.map(search => (
                  <motion.button
                    key={search}
                    onClick={() => handlePopularSearch(search)}
                    className="bg-white/20 px-3 py-1 rounded-full text-sm hover:bg-white/30 transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {search}
                  </motion.button>
                ))}
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Main Content Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
          {/* Global Loading Indicator */}
          <AnimatePresence>
            {isFiltering && (
              <motion.div 
                className="mb-6 p-4 bg-blue-50/80 backdrop-blur-sm border border-blue-200/50 rounded-xl shadow-lg"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <div className="flex items-center justify-center text-blue-700">
                  <Loader2 className="w-6 h-6 animate-spin mr-3" />
                  <span className="font-medium">Updating results...</span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          
          {/* Results Header */}
          <motion.div 
            className="mb-8"
            variants={fadeInUp}
            initial="hidden"
            animate="show"
          >
            <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-6">
              <div>
                <motion.h2 
                  className="text-2xl md:text-3xl font-bold text-gray-900 mb-2"
                  variants={fadeInUp}
                >
                  <UsersIcon className="inline w-6 h-6 mr-2 text-purple-600" />
                  Therapists in {locationInput || 'Your Area'}
                </motion.h2>
                <motion.p 
                  className="text-gray-600 text-lg"
                  variants={fadeInUp}
                >
                  {filteredClinicians.length > 0 
                    ? `Showing ${filteredClinicians.length} of ${totalResults} providers${pagination ? ` (Page ${currentPageNum} of ${totalPages})` : ''}`
                    : 'No clinicians available'
                  }
                </motion.p>
              </div>
              <motion.div 
                className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mt-4 lg:mt-0"
                variants={slideInRight}
              >
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700">Sort by:</label>
                  <select
                    className="border border-gray-300 rounded-xl px-4 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white/70 backdrop-blur-sm"
                    value={sortBy}
                    onChange={handleSortChange}
                  >
                    <option value="best-match">Best Match</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                    <option value="available">Available Today</option>
                  </select>
                </div>
                <motion.button 
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="flex items-center gap-2 border border-purple-600 text-purple-600 px-4 py-2 rounded-xl text-sm hover:bg-purple-50 transition-all duration-300 bg-white/70 backdrop-blur-sm"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Filter className="w-4 h-4" />
                  Refine Search
                </motion.button>
              </motion.div>
            </div>
          </motion.div>

          {/* Best Match Card */}
          <AnimatePresence>
            {filteredClinicians.length > 0 && (
              <motion.div 
                className="bg-gradient-to-r from-green-50/80 to-blue-50/80 backdrop-blur-sm border border-green-200/50 rounded-2xl p-6 mb-8 shadow-xl"
                variants={cardVariants}
                initial="hidden"
                animate="show"
                whileHover="hover"
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center shadow-lg">
                    <Award className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Best Match for You</h3>
                    <p className="text-green-700 flex items-center gap-1">
                      <Sparkles className="w-4 h-4" />
                      Based on your search criteria and preferences
                    </p>
                  </div>
                </div>
                <div className="bg-white/70 backdrop-blur-sm rounded-xl p-6 border border-white/20 shadow-lg">
                  <div className="flex flex-col lg:flex-row items-start gap-6">
                    <div className="w-20 h-20 bg-gradient-to-br from-purple-400 to-pink-500 rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-lg">
                      {filteredClinicians[0].fullName ? filteredClinicians[0].fullName.split(' ').map((n: string) => n[0]).join('') : 'NA'}
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-3">
                        <h4 className="text-2xl font-bold text-gray-900">{filteredClinicians[0].fullName}</h4>
                        <span className="px-4 py-1 rounded-full text-sm font-semibold border border-green-200 bg-green-100 text-green-800 flex items-center gap-1 w-fit">
                          <Star className="w-4 h-4" />
                          Excellent Match
                        </span>
                      </div>
                      <p className="text-gray-600 text-lg mb-2">{filteredClinicians[0].title || 'Licensed Therapist'}</p>
                      <p className="text-gray-700 text-base mb-4">{filteredClinicians[0].specialties?.join(', ') || 'General Therapy'}</p>
                      <div className="flex flex-wrap items-center gap-6 text-sm text-gray-600 mb-4">
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-5 h-5 text-green-600" />
                          <span className="font-medium">$120-150</span>
                        </div>
                        <div className="flex items-center gap-2 text-green-600">
                          <Clock className="w-5 h-5" />
                          <span className="font-medium">Available today</span>
                        </div>
                        <div className="flex items-center gap-2 text-blue-600">
                          <Wifi className="w-5 h-5" />
                          <span className="font-medium">Online & In-person</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-3 w-full lg:w-auto">
                      <motion.button
                        onClick={async () => {
                          await trackProfileButtonClick('profile_click', {
                            clinicianId: filteredClinicians[0].id,
                            clinicianName: filteredClinicians[0].fullName,
                            clinicianSlug: filteredClinicians[0].slug,
                            matchType: 'best_match',
                            position: 'best_match_card',
                            practiceId: filteredClinicians[0].practice_id || filteredClinicians[0].id_practice,
                            score: filteredClinicians[0].score,
                          });
                          window.location.href = `/clinician/${filteredClinicians[0].slug}`;
                        }}
                        className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-8 py-3 rounded-xl text-sm font-semibold hover:from-purple-700 hover:to-indigo-700 transition-all duration-300 transform hover:scale-105 hover:shadow-lg flex items-center justify-center gap-2"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <Eye className="w-4 h-4" />
                        View Profile
                      </motion.button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Therapist Cards Grid */}
          <motion.div 
            className="space-y-6"
            variants={containerVariants}
            initial="hidden"
            animate="show"
          >
            {filteredClinicians.length === 0 ? (
              <motion.div 
                className="text-center py-12"
                variants={fadeInUp}
              >
                <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-8 border border-white/20 shadow-xl max-w-md mx-auto">
                  <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <UsersIcon className="w-10 h-10 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">No Clinicians Available</h3>
                  <p className="text-gray-600 mb-6">
                    {loading ? 'Loading clinicians...' : 'No clinicians found matching your criteria.'}
                  </p>
                  {!loading && (
                    <motion.button 
                      onClick={() => runSearch(1)}
                      className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-8 py-3 rounded-xl font-semibold hover:from-purple-700 hover:to-indigo-700 transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Zap className="inline w-4 h-4 mr-2" />
                      Try Again
                    </motion.button>
                  )}
                </div>
              </motion.div>
            ) : (
              filteredClinicians.map((therapist, index) => (
                <motion.div
                  key={therapist.id || therapist._id}
                  data-therapist-card
                  data-therapist-id={therapist.id || therapist._id}
                  data-therapist-index={index}
                  variants={cardVariants}
                  whileHover="hover"
                  className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/20 hover:bg-white/80 transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  <div className="p-6 md:p-8">
                    <div className="flex flex-col lg:flex-row gap-6">
                      <div className="w-24 h-24 lg:w-32 lg:h-32 bg-gradient-to-br from-purple-400 to-pink-500 rounded-xl flex items-center justify-center text-white font-bold text-xl lg:text-2xl flex-shrink-0 shadow-lg">
                        {therapist.fullName ? therapist.fullName.split(' ').map((n: string) => n[0]).join('') : 'NA'}
                      </div>
                      <div className="flex-1">
                        <div className="flex flex-col lg:flex-row lg:items-start justify-between mb-4">
                          <div className="flex-1">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-2">
                              <h3 className="text-xl lg:text-2xl font-bold text-gray-900">{therapist.fullName}</h3>
                              <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1 w-fit">
                                <CheckCircle className="w-4 h-4" />
                                Available
                              </span>
                            </div>
                            <p className="text-gray-600 text-lg mb-2">{therapist.title || 'Licensed Therapist'}</p>
                            <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                              <div className="flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-purple-600" />
                                <span className="font-medium">{therapist?.licenses[0]?.state || 'Not Available'}</span>
                              </div>
                              <div className="flex items-center gap-2 text-blue-600">
                                <Wifi className="w-4 h-4" />
                                <span className="font-medium">Online & In-person</span>
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="px-4 py-3 rounded-xl border text-center bg-gradient-to-br from-green-100 to-emerald-100 text-green-800 border-green-200 shadow-lg">
                              <div className="text-2xl font-bold">{therapist.score != null ? `${Math.min(Math.round(therapist.score * 100), 100)}%` : 'N/A'}</div>
                              <div className="text-xs font-semibold">Match Score</div>
                            </div>
                          </div>
                        </div>
                        <div className="mb-4">
                          <p className="text-sm text-gray-700 font-semibold mb-2 flex items-center gap-1">
                            <Target className="w-4 h-4 text-purple-600" />
                            Specializes in:
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {therapist.specialties?.map((specialty: string) => (
                              <span key={specialty} className="bg-purple-50 text-purple-700 px-3 py-1 rounded-lg text-sm font-medium border border-purple-200">
                                {specialty}
                              </span>
                            ))}
                          </div>
                        </div>
                        <p className="text-gray-700 text-base leading-relaxed mb-6">
                          {therapist.description || "I am a licensed Clinical Social Worker who specializes in working with adults. I have over 15 years of experience in the field of Behavioral Health and Social Services. My professional background incl..."}
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
                          <div className="bg-gray-50/50 rounded-lg p-3">
                            <p className="font-semibold text-gray-900 mb-1 flex items-center gap-1">
                              <DollarSign className="w-4 h-4 text-green-600" />
                              Session Rate
                            </p>
                            <p className="text-gray-600">{therapist.rate || '$120-150'}</p>
                          </div>
                          <div className="bg-gray-50/50 rounded-lg p-3">
                            <p className="font-semibold text-gray-900 mb-1 flex items-center gap-1">
                              <User className="w-4 h-4 text-blue-600" />
                              Approaches
                            </p>
                            <p className="text-gray-600">{therapist.approaches?.join(', ') || 'Cognitive Behavioral (CBT), EMDR'}</p>
                          </div>
                          <div className="bg-gray-50/50 rounded-lg p-3">
                            <p className="font-semibold text-gray-900 mb-1 flex items-center gap-1">
                              <Globe className="w-4 h-4 text-indigo-600" />
                              Languages
                            </p>
                            <p className="text-gray-600">{therapist.languages?.join(', ') || 'English, Spanish'}</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col gap-3 min-w-[140px]">
                        <motion.button
                          onClick={async () => {
                            await trackProfileButtonClick('profile_click', {
                              clinicianId: therapist.id,
                              clinicianName: therapist.fullName,
                              clinicianSlug: therapist.slug,
                              matchType: 'therapist_card',
                              position: `card_${index + 1}`,
                              therapistIndex: index,
                              practiceId: therapist.practice_id || therapist.id_practice,
                              score: therapist.score,
                            });
                            window.location.href = `/clinician/${therapist.slug}`;
                          }}
                          className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 py-3 rounded-xl text-sm font-semibold hover:from-purple-700 hover:to-indigo-700 transition-all duration-300 transform hover:scale-105 hover:shadow-lg flex items-center justify-center gap-2"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <Eye className="w-4 h-4" />
                          View Profile
                        </motion.button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </motion.div>

          {/* Enhanced Pagination */}
          {pagination && totalPages > 1 && (
            <motion.div 
              className="flex justify-center items-center gap-4 mt-12"
              variants={fadeInUp}
              initial="hidden"
              animate="show"
            >
              <motion.button
                onClick={() => handlePageChange(currentPageNum - 1)}
                disabled={currentPageNum === 1}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 bg-white/70 backdrop-blur-sm"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <ArrowLeft className="w-4 h-4" />
                Previous
              </motion.button>
              
              <div className="flex items-center gap-2">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = i + 1;
                  return (
                    <motion.button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${
                        currentPageNum === pageNum
                          ? 'bg-purple-600 text-white shadow-lg'
                          : 'border border-gray-300 text-gray-700 hover:bg-gray-50 bg-white/70 backdrop-blur-sm'
                      }`}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {pageNum}
                    </motion.button>
                  );
                })}
              </div>
              
              <motion.button
                onClick={() => handlePageChange(currentPageNum + 1)}
                disabled={currentPageNum === totalPages}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 bg-white/70 backdrop-blur-sm"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Next
                <ArrowRight className="w-4 h-4" />
              </motion.button>
            </motion.div>
          )}
        </div>
      </div>
    </>
  );
};
